import { debounce as debounceFn } from 'debounce';

import { handleMapClick } from '@src/layers/polygons';
import getFilterStore from '@stores/FilterStore';
import getUIStore from '@stores/UIStore';
import * as polygons from '../layers/polygons';
import * as properties from '../layers/properties';
import { MAP_LAYERS, MAPBOX_STYLE, ZOOM_LEVELS } from './const';
import { filterViewModel } from './filterViewModel';
import { mapboxgl } from './MapBox';

import type { ApolloClient } from '@lib/apolloClient';
import type { LatLngCoordinates } from '@lib/types';

type LayerLevel = keyof typeof ZOOM_LEVELS;
const UIStore = getUIStore();
const FilterStore = getFilterStore();

export const initializeMap = (
  coordinates: mapboxgl.MapboxOptions['center'],
  layerLevel: LayerLevel = 'country',
  zoom: number = ZOOM_LEVELS[layerLevel],
  apolloClient: ApolloClient,
  loadCallback?: () => void,
): void => {
  const mapbox = new mapboxgl.Map({
    container: 'map',
    style: MAPBOX_STYLE,
    center: coordinates,
    zoom,
    minZoom: ZOOM_LEVELS.country,
    pitchWithRotate: false,
    dragRotate: false,
    keyboard: false,
  });

  mapbox.touchZoomRotate.disableRotation();

  window.Mapbox = mapbox;

  const navigationControl = new mapboxgl.NavigationControl({
    showCompass: false,
  });
  mapbox.addControl(navigationControl, 'bottom-right');
  const markerFromQS = filterViewModel.marker;

  const markerEl = document.getElementById('marker');
  const marker = new mapboxgl.Marker(markerEl || undefined);

  const popup = new mapboxgl.Popup({
    anchor: 'bottom',
    closeOnClick: true,
    maxWidth: 'none',
  } as mapboxgl.PopupOptions);

  window.Popup = popup;
  window.Marker = marker;

  const handleProperties = (): void => {
    if (properties.isPropertyLevel()) {
      properties.updateProperties(apolloClient);
      properties.showProperties();
    } else {
      properties.hideProperties();
    }
  };

  const debouncedFilterSubmit = debounceFn(() => {
    try {
      filterViewModel.submit();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        e instanceof Error ? e.message : '',
        JSON.stringify({
          current: {
            center: filterViewModel.center,
            marker: filterViewModel.marker,
          },
          model: filterViewModel.model,
        }),
      );
    }
  }, 1000);

  const debouncedMoveEnd = debounceFn(async () => {
    const center = mapbox.getCenter().toArray() as LatLngCoordinates;

    if (!areCoordinatesSame(filterViewModel.center?.toString(), center.toString())) {
      filterViewModel.center = center;
    }

    if (filterViewModel.isDirty) {
      debouncedFilterSubmit();
    }

    handleProperties();

    UIStore.registerFilters(FilterStore, { debounce: 500 });
  }, 500);

  mapbox.on('load', () => {
    properties.init(mapbox);
    polygons.init(mapbox);
    handleProperties();

    if (markerFromQS) {
      polygons.handleMapClick(apolloClient, mapbox, marker, markerFromQS, false);
    }

    loadCallback?.();
  });

  mapbox.on('click', MAP_LAYERS.propertiesClustered, (event) => {
    event.preventDefault();

    popup.remove();

    properties.handleClusterClick(mapbox, event.point);
  });

  mapbox.on('click', MAP_LAYERS.propertiesUnclustered, (event) => {
    event.preventDefault();

    properties.handlePropertyClick(mapbox, event.point, popup);
  });

  mapbox.on(
    'click',
    debounceFn(async (event) => {
      if (event.defaultPrevented) {
        return;
      }

      if (properties.isPropertyLevel()) {
        debouncedFilterSubmit.clear();
        filterViewModel.reset();
        polygons.handleMapClick(
          apolloClient,
          mapbox,
          marker,
          event.lngLat.toArray() as LatLngCoordinates,
          true,
        );
      }
    }, 300),
  );

  mapbox.on('zoomend', async () => {
    if (!properties.isPropertyLevel()) {
      popup.remove();
      filterViewModel.marker = null;
      polygons.hideMarker(marker);
      polygons.hidePolygon(mapbox);
    }

    filterViewModel.zoom = parseInt(mapbox.getZoom().toFixed(), 10);
  });

  mapbox.on('movestart', async () => {
    debouncedMoveEnd.clear();
    debouncedFilterSubmit.clear();

    if (popup) {
      popup.remove();
    }
  });

  mapbox.on('zoomstart', async () => {
    debouncedMoveEnd.clear();
    debouncedFilterSubmit.clear();
  });

  mapbox.on('moveend', debouncedMoveEnd);
};

/**
 * Returns true if given two coordinates are same.
 * @param coordinateOne
 * @param coordinateTwo
 */
function areCoordinatesSame(coordinateOne?: string, coordinateTwo?: string): boolean {
  if (!coordinateOne || !coordinateTwo) {
    return false;
  }

  const first = coordinateOne.split(',');
  const second = coordinateTwo.split(',');

  if (first.length !== 2 || second.length !== 2) {
    throw Error('Incorrect coordinates');
  }

  return isLngLatSame(first[0], second[0]) && isLngLatSame(first[1], second[1]);
}

/**
 * Returns true if given two lat|lng are same. It compares the first eight decimal.
 * @param coordinateOne lng or lat
 * @param coordinateTwo lng or lat
 */
function isLngLatSame(coordinateOne: string, coordinateTwo: string): boolean {
  if (!coordinateOne || !coordinateTwo) {
    return false;
  }

  const first = coordinateOne.split('.');
  const second = coordinateTwo.split('.');

  if (first.length !== 2 || second.length !== 2) {
    throw Error('Incorrect coordinates');
  }

  if (first[0] !== second[0]) {
    return false;
  }

  return first[1].substring(0, 8) === second[1].substring(0, 8);
}

export const flyTo = (center: LatLngCoordinates, zoom?: number): void => {
  window.Mapbox.jumpTo({
    center,
    zoom,
  });
};

export function moveMap(apolloClient: ApolloClient): void {
  const center = filterViewModel.center;
  const zoom = filterViewModel.zoom;

  if (window?.Mapbox && center) {
    if (center.toString() !== window.Mapbox.getCenter().toArray().toString()) {
      flyTo(center, zoom ?? undefined);
      if (filterViewModel.marker && window.Marker) {
        handleMapClick(apolloClient, window.Mapbox, window.Marker, filterViewModel.marker, false);
      }
    }
  }
}
